import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useI18n, type TFunction } from "@faceheart/i18n";
import { alertsApi } from "../lib/api/endpoints";
import { formatAlertMessage, formatAlertType } from "../lib/alerts/format";
import type { Alert } from "@faceheart/api-types";

type Severity = "critical" | "warning" | "info" | "all";
type AckFilter = "all" | "open" | "acked";
type TypeFilter = "all" | "hardcoded" | "rule" | "deviation" | "quality";

export function AlertsPage() {
  const { t, i18n } = useI18n("admin");
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [sev, setSev] = useState<Severity>("all");
  const [ack, setAck] = useState<AckFilter>("open");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const pageSize = 25;
  const dateLocale = i18n.language === "en" ? "en-US" : "zh-TW";

  // Selection is per-page: clear it whenever the page or any filter changes so a
  // hidden selection can't be acted on. These helpers wrap the setters.
  const clearSel = () => setSelectedIds(new Set());
  const changePage = (p: number) => { clearSel(); setPage(p); };

  const listQ = useQuery({
    queryKey: ["alerts", "list", { page, sev, ack, typeFilter }],
    queryFn: () =>
      alertsApi.list({
        page,
        page_size: pageSize,
        severity: sev === "all" ? undefined : sev,
        acknowledged: ack === "all" ? undefined : ack === "acked",
      }),
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => alertsApi.acknowledge(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["alerts"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(t("alerts.ack.success"));
    },
    onError: (err) =>
      toast.error(t("alerts.ack.failed"), { description: (err as Error).message }),
  });

  const bulkAckMut = useMutation({
    mutationFn: (ids: string[]) => alertsApi.acknowledgeBulk(ids),
    onSuccess: (res) => {
      clearSel();
      void qc.invalidateQueries({ queryKey: ["alerts"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(t("alerts.ack.bulkSuccess", { count: res.acknowledged }));
    },
    onError: (err) =>
      toast.error(t("alerts.ack.failed"), { description: (err as Error).message }),
  });

  // Filter setters clear the per-page selection (a hidden selection must not be
  // actionable). page reset + selection clear go together.
  const applyAck = (v: AckFilter) => { clearSel(); setPage(1); setAck(v); };
  const applySev = (v: Severity) => { clearSel(); setPage(1); setSev(v); };
  const applyType = (v: TypeFilter) => { clearSel(); setTypeFilter(v); };

  // type filter is client-side because the backend doesn't expose it (yet).
  const items: Alert[] = (listQ.data?.items ?? []).filter((a) => {
    if (typeFilter === "all") return true;
    if (typeFilter === "hardcoded") return a.type.startsWith("hardcoded:");
    if (typeFilter === "rule") return a.type.startsWith("rule:");
    if (typeFilter === "deviation") return a.type.startsWith("personal_deviation_");
    if (typeFilter === "quality") return a.type.startsWith("measurement_quality:");
    return true;
  });

  // Only unacknowledged rows are selectable for bulk-ack.
  const selectableIds = items.filter((a) => !a.acknowledged).map((a) => a.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectableIds.some((id) => selectedIds.has(id));

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(selectableIds));

  return (
    <div className="p-8">
      <h1 className="text-xl font-medium mb-6">{t("alerts.title")}</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <Chip active={ack === "open"} onClick={() => applyAck("open")}>
          {t("alerts.filters.ackOpen")}
        </Chip>
        <Chip active={ack === "acked"} onClick={() => applyAck("acked")}>
          {t("alerts.filters.ackAcked")}
        </Chip>
        <Chip active={ack === "all"} onClick={() => applyAck("all")}>
          {t("alerts.filters.ackAll")}
        </Chip>

        <span className="mx-2 border-l border-admin-border" />

        <Chip active={sev === "all"} onClick={() => applySev("all")}>
          {t("alerts.filters.sevAll")}
        </Chip>
        <Chip active={sev === "critical"} onClick={() => applySev("critical")}>
          {t("alerts.filters.sevCritical")}
        </Chip>
        <Chip active={sev === "warning"} onClick={() => applySev("warning")}>
          {t("alerts.filters.sevWarning")}
        </Chip>
        <Chip active={sev === "info"} onClick={() => applySev("info")}>
          {t("alerts.filters.sevInfo")}
        </Chip>

        <span className="mx-2 border-l border-admin-border" />

        <Chip active={typeFilter === "all"} onClick={() => applyType("all")}>
          {t("alerts.filters.typeAll")}
        </Chip>
        <Chip active={typeFilter === "hardcoded"} onClick={() => applyType("hardcoded")}>
          {t("alerts.filters.typeHardcoded")}
        </Chip>
        <Chip active={typeFilter === "rule"} onClick={() => applyType("rule")}>
          {t("alerts.filters.typeRule")}
        </Chip>
        <Chip active={typeFilter === "deviation"} onClick={() => applyType("deviation")}>
          {t("alerts.filters.typeDeviation")}
        </Chip>
        <Chip active={typeFilter === "quality"} onClick={() => applyType("quality")}>
          {t("alerts.filters.typeQuality")}
        </Chip>
      </div>

      {listQ.isLoading && <div className="text-admin-text2 text-sm">{t("alerts.loading")}</div>}
      {listQ.error && (
        <div className="text-admin-red text-sm">
          {t("alerts.loadFailed", { message: (listQ.error as Error).message })}
        </div>
      )}

      {listQ.data && (
        <>
          {/* Select-all + bulk action bar. Only shown when there are selectable
              (unacknowledged) rows on this page. */}
          {selectableIds.length > 0 && (
            <div className="flex items-center gap-3 text-sm">
              <label className="inline-flex items-center gap-2 text-admin-text2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-admin-cyan"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={toggleAll}
                />
                {t("alerts.bulk.selectAll")}
              </label>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2 rounded-md border border-admin-cyan/40 bg-admin-cyan/15 px-3 py-1.5">
                  <span className="text-admin-text1">
                    {t("alerts.bulk.selectedCount", { count: selectedIds.size })}
                  </span>
                  <button
                    type="button"
                    onClick={() => bulkAckMut.mutate([...selectedIds])}
                    disabled={bulkAckMut.isPending}
                    className="inline-flex items-center gap-1 text-xs text-admin-cyan hover:underline disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" /> {t("alerts.bulk.ackBtn")}
                  </button>
                  <button
                    type="button"
                    onClick={clearSel}
                    className="text-xs text-admin-text3 hover:underline"
                  >
                    {t("alerts.bulk.clear")}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="bg-admin-bg2 border border-admin-border rounded-lg divide-y divide-admin-border">
            {items.length === 0 ? (
              <div className="text-center py-12 text-admin-text3 text-sm">
                {t("alerts.empty")}
              </div>
            ) : (
              items.map((a) => (
                <div
                  key={a.id}
                  className="p-4 flex items-start gap-3 hover:bg-admin-bg3/40"
                >
                  {/* Checkbox for unacked rows; a spacer keeps acked rows aligned. */}
                  {!a.acknowledged ? (
                    <input
                      type="checkbox"
                      className="mt-1 accent-admin-cyan"
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleOne(a.id)}
                      aria-label={t("alerts.bulk.selectRow")}
                    />
                  ) : (
                    <span className="w-3.5 shrink-0" aria-hidden />
                  )}
                  <SeverityDot s={a.severity} />
                  <div className="flex-1 min-w-0">
                    {/* Employee identity — the whole point: who is this alert for. */}
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <Link
                        to={`/employees/${a.employee_id}`}
                        className="text-sm font-medium text-admin-cyan hover:underline truncate"
                      >
                        {a.employee_name || a.employee_code || t("alerts.row.unknownEmployee")}
                      </Link>
                      {a.employee_code && (
                        <span className="text-xs text-admin-text3 shrink-0">{a.employee_code}</span>
                      )}
                    </div>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="text-sm text-admin-text1">{formatAlertMessage(t, a)}</div>
                      <div className="text-xs text-admin-text3 shrink-0">
                        {new Date(a.created_at).toLocaleString(dateLocale)}
                      </div>
                    </div>
                    <div className="text-xs text-admin-text3 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{t("alerts.row.typeLabel", { type: formatAlertType(t, a.type) })}</span>
                      <span>
                        {a.metric} = {a.metric_value}
                      </span>
                      {a.threshold != null && (
                        <span>{t("alerts.row.thresholdLabel", { threshold: a.threshold })}</span>
                      )}
                      {a.baseline_mean != null && a.baseline_sd != null && (
                        <span className="text-admin-orange">
                          {t("alerts.row.baselineLabel", {
                            mean: a.baseline_mean.toFixed(1),
                            sd: a.baseline_sd.toFixed(2),
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  {!a.acknowledged ? (
                    <button
                      type="button"
                      onClick={() => ackMut.mutate(a.id)}
                      disabled={ackMut.isPending}
                      className="shrink-0 inline-flex items-center gap-1 text-xs text-admin-cyan hover:underline disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" /> {t("alerts.ack.btn")}
                    </button>
                  ) : (
                    <span className="shrink-0 text-xs text-admin-text3">
                      {t("alerts.ack.ackedTag", {
                        at: a.acknowledged_at
                          ? new Date(a.acknowledged_at).toLocaleString(dateLocale)
                          : "",
                      })}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          <Pagination
            page={listQ.data.page}
            pageSize={listQ.data.page_size}
            total={listQ.data.total}
            onChange={changePage}
            t={t}
          />
        </>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 text-xs rounded transition-colors ${
        active
          ? "bg-admin-cyan/15 text-admin-cyan border border-admin-cyan/40"
          : "border border-admin-border text-admin-text2 hover:border-admin-border2"
      }`}
    >
      {children}
    </button>
  );
}

function SeverityDot({ s }: { s: string }) {
  const cls =
    s === "critical"
      ? "bg-admin-red"
      : s === "warning"
        ? "bg-admin-orange"
        : "bg-admin-cyan";
  return <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${cls}`} />;
}

function Pagination({
  page,
  pageSize,
  total,
  onChange,
  t,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (p: number) => void;
  t: TFunction;
}) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-admin-text2">
      <span>{t("alerts.pagination.summary", { total, page, last })}</span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="px-3 py-1.5 border border-admin-border rounded hover:bg-admin-bg3 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t("alerts.pagination.prev")}
        </button>
        <button
          type="button"
          disabled={page >= last}
          onClick={() => onChange(page + 1)}
          className="px-3 py-1.5 border border-admin-border rounded hover:bg-admin-bg3 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t("alerts.pagination.next")}
        </button>
      </div>
    </div>
  );
}
